import { api } from '@/apis';
import { log, startCase } from '@/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * A hook that fetches the result of an aggregation pipeline.
 * @param {string} collection - The collection to aggregate.
 * @param {Array.<object>} pipeline - The pipeline to use for aggregation.
 * @param {boolean} [enabled] - Whether the query is enabled.
 * @param {number} [staleTime] - The stale time for the query.
 * @param {number} [gcTime] - The garbage collection time for the query.
 * @returns {import('@tanstack/react-query').UseQueryResult<object[]> & { cancel: () => Promise<void> }}
 */
export function useAggregate(collection, pipeline, enabled, staleTime, gcTime) {
  const queryKey = [collection, 'aggregate', pipeline];
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      log('Read', startCase(collection), { pipeline });

      return api
        .post(`pipeline/${collection}`, { json: pipeline, signal })
        .json();
    },
    placeholderData: (previousData) => previousData ?? [],
    staleTime,
    gcTime,
    enabled: !!enabled,
  });

  return {
    ...query,
    cancel: () => queryClient.cancelQueries({ queryKey }),
  };
}
